// Templated HTML generator for the /demo experience. Pure-frontend — no
// LLM call. Fills a single-page business site template with the lead's
// mock data so the "Generate Site" flow ends with something tangible.

import type { DemoLead } from './mockLeadsData';

interface Args {
  lead: DemoLead;
  templateType: string;
  selectedFeatures: string[];
  services: string[];
}

const PALETTES: Record<string, { primary: string; accent: string; bg: string; text: string }> = {
  default: { primary: '#5B3A3A', accent: '#B08080', bg: '#FAF7F5', text: '#1A1A1A' },
  'bold-corporate': { primary: '#1E3A5F', accent: '#3B82F6', bg: '#FFFFFF', text: '#0F172A' },
  'modern-minimal': { primary: '#000000', accent: '#525252', bg: '#FAFAFA', text: '#171717' },
  'warm-friendly': { primary: '#C2410C', accent: '#EA580C', bg: '#FFF7ED', text: '#431407' },
  'tech-startup': { primary: '#7C3AED', accent: '#A78BFA', bg: '#0F0F23', text: '#F1F5F9' },
  'elegant-luxury': { primary: '#78350F', accent: '#D4AF37', bg: '#FAF6EE', text: '#1C1917' },
  'playful-creative': { primary: '#DB2777', accent: '#FB7185', bg: '#FFF1F2', text: '#500724' },
};

function paletteFor(template: string) {
  return PALETTES[template] || PALETTES.default;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function star(n: number): string {
  return '★'.repeat(Math.round(n));
}

export function generateMockSiteHtml({ lead, templateType, selectedFeatures, services }: Args): string {
  const palette = paletteFor(templateType);
  const isDark = templateType === 'tech-startup';
  const niceNiche = lead.niche.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const heroImage = lead.businessImages[0];
  const galleryImages = lead.businessImages.slice(1, 4);
  const showsReviews = selectedFeatures.includes('reviews') || selectedFeatures.length === 0;
  const showsGallery = selectedFeatures.includes('gallery') || lead.businessImages.length >= 2;

  const reviews = lead.googleReviews.slice(0, 3);

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(lead.businessName)} | ${escapeHtml(niceNiche)} in ${escapeHtml(lead.location)}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com" />
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    html, body { font-family: 'Inter', -apple-system, sans-serif; background: ${palette.bg}; color: ${palette.text}; line-height: 1.6; }
    img { max-width: 100%; display: block; }
    .container { max-width: 1140px; margin: 0 auto; padding: 0 24px; }
    a { color: ${palette.primary}; text-decoration: none; }

    /* Header */
    header { padding: 20px 0; border-bottom: 1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}; position: sticky; top: 0; background: ${palette.bg}; z-index: 50; }
    .header-row { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 16px; }
    .brand { font-size: 20px; font-weight: 800; color: ${palette.primary}; }
    nav ul { list-style: none; display: flex; gap: 28px; }
    nav a { font-weight: 500; font-size: 14px; color: ${palette.text}; opacity: 0.8; }
    .cta-btn { background: ${palette.primary}; color: ${isDark ? palette.text : '#fff'}; padding: 10px 22px; border-radius: 8px; font-weight: 600; font-size: 14px; }

    /* Hero */
    .hero { padding: 80px 0 60px; }
    .hero-grid { display: grid; grid-template-columns: 1.1fr 1fr; gap: 48px; align-items: center; }
    .eyebrow { color: ${palette.accent}; font-size: 13px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 16px; }
    h1 { font-size: 52px; font-weight: 800; line-height: 1.1; letter-spacing: -0.02em; margin-bottom: 20px; }
    .lead { font-size: 19px; opacity: 0.75; margin-bottom: 28px; }
    .hero-cta { display: flex; gap: 12px; flex-wrap: wrap; }
    .hero-cta .secondary { background: transparent; border: 1.5px solid ${palette.primary}; color: ${palette.primary}; padding: 10px 22px; border-radius: 8px; font-weight: 600; font-size: 14px; }
    .hero-img { aspect-ratio: 4/3; border-radius: 16px; overflow: hidden; box-shadow: 0 30px 60px -20px rgba(0,0,0,0.25); }
    .hero-img img { width: 100%; height: 100%; object-fit: cover; }
    .rating-badge { display: inline-flex; align-items: center; gap: 8px; background: ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}; padding: 8px 14px; border-radius: 999px; margin-bottom: 24px; font-size: 14px; font-weight: 500; }
    .stars { color: #F59E0B; }

    /* Sections */
    section { padding: 80px 0; }
    section.alt { background: ${isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.025)'}; }
    .section-head { text-align: center; max-width: 640px; margin: 0 auto 56px; }
    h2 { font-size: 36px; font-weight: 800; letter-spacing: -0.01em; margin-bottom: 12px; }
    .section-head p { opacity: 0.7; font-size: 17px; }

    /* Services */
    .services { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 24px; }
    .service-card { background: ${isDark ? 'rgba(255,255,255,0.04)' : '#fff'}; padding: 32px 28px; border-radius: 12px; border: 1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}; transition: transform 0.2s; }
    .service-card:hover { transform: translateY(-3px); }
    .service-icon { width: 44px; height: 44px; background: ${palette.primary}; color: ${isDark ? palette.text : '#fff'}; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-weight: 800; margin-bottom: 16px; }
    .service-card h3 { font-size: 18px; font-weight: 700; margin-bottom: 8px; }
    .service-card p { opacity: 0.7; font-size: 14px; }

    /* Gallery */
    .gallery { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 16px; }
    .gallery img { aspect-ratio: 4/3; object-fit: cover; border-radius: 12px; }

    /* Reviews */
    .reviews { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 24px; }
    .review { background: ${isDark ? 'rgba(255,255,255,0.04)' : '#fff'}; padding: 28px; border-radius: 12px; border: 1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}; }
    .review-stars { color: #F59E0B; margin-bottom: 12px; font-size: 16px; }
    .review-text { font-size: 15px; opacity: 0.85; margin-bottom: 16px; line-height: 1.7; }
    .review-author { font-weight: 600; font-size: 14px; }
    .review-time { opacity: 0.5; font-size: 13px; }

    /* Contact */
    .contact-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; max-width: 920px; margin: 0 auto; }
    .contact-info { display: flex; flex-direction: column; gap: 20px; }
    .contact-item { display: flex; gap: 14px; align-items: flex-start; }
    .contact-icon { width: 40px; height: 40px; background: ${palette.primary}; color: ${isDark ? palette.text : '#fff'}; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 18px; }
    .contact-label { font-size: 12px; text-transform: uppercase; letter-spacing: 0.08em; opacity: 0.6; margin-bottom: 4px; }
    .contact-value { font-weight: 600; font-size: 16px; }
    .contact-form { background: ${isDark ? 'rgba(255,255,255,0.04)' : '#fff'}; padding: 32px; border-radius: 16px; border: 1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}; }
    .contact-form h3 { font-size: 22px; font-weight: 700; margin-bottom: 6px; }
    .contact-form p { opacity: 0.7; font-size: 14px; margin-bottom: 20px; }
    .field { margin-bottom: 16px; }
    label { display: block; font-size: 13px; font-weight: 500; margin-bottom: 6px; opacity: 0.8; }
    input, textarea { width: 100%; padding: 12px 14px; border: 1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}; border-radius: 8px; font-family: inherit; font-size: 14px; background: ${isDark ? 'rgba(0,0,0,0.3)' : palette.bg}; color: ${palette.text}; }
    textarea { resize: vertical; min-height: 100px; }
    button.submit { width: 100%; background: ${palette.primary}; color: ${isDark ? palette.text : '#fff'}; padding: 14px; border: none; border-radius: 8px; font-weight: 600; font-size: 15px; cursor: pointer; }

    /* Footer */
    footer { padding: 40px 0 32px; border-top: 1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}; text-align: center; opacity: 0.6; font-size: 14px; }

    /* Responsive */
    @media (max-width: 768px) {
      .hero { padding: 48px 0 40px; }
      .hero-grid, .contact-grid { grid-template-columns: 1fr; gap: 32px; }
      h1 { font-size: 36px; }
      h2 { font-size: 28px; }
      section { padding: 56px 0; }
      nav ul { display: none; }
    }
  </style>
</head>
<body>
  <header>
    <div class="container header-row">
      <div class="brand">${escapeHtml(lead.businessName)}</div>
      <nav><ul>
        <li><a href="#services">Services</a></li>
        ${showsGallery ? '<li><a href="#gallery">Gallery</a></li>' : ''}
        ${showsReviews ? '<li><a href="#reviews">Reviews</a></li>' : ''}
        <li><a href="#contact">Contact</a></li>
      </ul></nav>
      <a href="#contact" class="cta-btn">Get a Quote</a>
    </div>
  </header>

  <section class="hero">
    <div class="container hero-grid">
      <div>
        <div class="eyebrow">${escapeHtml(niceNiche)} • ${escapeHtml(lead.location)}</div>
        <h1>${escapeHtml(lead.businessName)}</h1>
        <p class="lead">Trusted local ${escapeHtml(niceNiche.toLowerCase())} serving ${escapeHtml(lead.location)} and surrounding areas. Honest pricing, real expertise, and work that lasts.</p>
        ${lead.rating ? `<div class="rating-badge"><span class="stars">${star(lead.rating)}</span> ${lead.rating} stars · ${lead.reviewCount} Google reviews</div>` : ''}
        <div class="hero-cta">
          <a href="#contact" class="cta-btn">Request a Quote</a>
          ${lead.phone ? `<a href="tel:${escapeHtml(lead.phone)}" class="secondary">Call ${escapeHtml(lead.phone)}</a>` : ''}
        </div>
      </div>
      ${heroImage ? `<div class="hero-img"><img src="${escapeHtml(heroImage)}" alt="${escapeHtml(lead.businessName)}" /></div>` : ''}
    </div>
  </section>

  <section id="services" class="alt">
    <div class="container">
      <div class="section-head">
        <h2>What we do</h2>
        <p>Full-service ${escapeHtml(niceNiche.toLowerCase())} for homes and businesses across ${escapeHtml(lead.location)}.</p>
      </div>
      <div class="services">
        ${services.map((s, i) => `
          <div class="service-card">
            <div class="service-icon">${i + 1}</div>
            <h3>${escapeHtml(s)}</h3>
            <p>Professional ${escapeHtml(s.toLowerCase())} done right the first time. Backed by our satisfaction guarantee.</p>
          </div>`).join('')}
      </div>
    </div>
  </section>

  ${showsGallery && galleryImages.length > 0 ? `
  <section id="gallery">
    <div class="container">
      <div class="section-head">
        <h2>Recent work</h2>
        <p>A look at projects we've completed in the ${escapeHtml(lead.location)} area.</p>
      </div>
      <div class="gallery">
        ${galleryImages.map((img) => `<img src="${escapeHtml(img)}" alt="${escapeHtml(lead.businessName)} project" />`).join('')}
      </div>
    </div>
  </section>` : ''}

  ${showsReviews && reviews.length > 0 ? `
  <section id="reviews" class="alt">
    <div class="container">
      <div class="section-head">
        <h2>What our customers say</h2>
        <p>${reviews.length}+ reviews from real customers in ${escapeHtml(lead.location)}.</p>
      </div>
      <div class="reviews">
        ${reviews.map((r) => `
          <div class="review">
            <div class="review-stars">${star(r.rating)}</div>
            <p class="review-text">"${escapeHtml(r.text)}"</p>
            <div class="review-author">${escapeHtml(r.authorName)}</div>
            ${r.relativeTime ? `<div class="review-time">${escapeHtml(r.relativeTime)}</div>` : ''}
          </div>`).join('')}
      </div>
    </div>
  </section>` : ''}

  <section id="contact">
    <div class="container">
      <div class="section-head">
        <h2>Get in touch</h2>
        <p>Tell us what you need — we'll get back within one business day.</p>
      </div>
      <div class="contact-grid">
        <div class="contact-info">
          ${lead.phone ? `
          <div class="contact-item">
            <div class="contact-icon">☎</div>
            <div>
              <div class="contact-label">Phone</div>
              <div class="contact-value"><a href="tel:${escapeHtml(lead.phone)}">${escapeHtml(lead.phone)}</a></div>
            </div>
          </div>` : ''}
          ${lead.email ? `
          <div class="contact-item">
            <div class="contact-icon">✉</div>
            <div>
              <div class="contact-label">Email</div>
              <div class="contact-value"><a href="mailto:${escapeHtml(lead.email)}">${escapeHtml(lead.email)}</a></div>
            </div>
          </div>` : ''}
          <div class="contact-item">
            <div class="contact-icon">📍</div>
            <div>
              <div class="contact-label">Service area</div>
              <div class="contact-value">${escapeHtml(lead.location)}</div>
            </div>
          </div>
          <div class="contact-item">
            <div class="contact-icon">⏰</div>
            <div>
              <div class="contact-label">Hours</div>
              <div class="contact-value" style="font-size:14px; font-weight:500;">${lead.businessHours.map((h) => escapeHtml(h)).join('<br/>')}</div>
            </div>
          </div>
        </div>
        <form class="contact-form" onsubmit="event.preventDefault(); alert('This is a demo — your message would be sent in the live site.');">
          <h3>Request a quote</h3>
          <p>Free, no-obligation estimate.</p>
          <div class="field"><label>Name</label><input type="text" required /></div>
          <div class="field"><label>Phone</label><input type="tel" required /></div>
          <div class="field"><label>What do you need?</label><textarea required></textarea></div>
          <button class="submit" type="submit">Send request</button>
        </form>
      </div>
    </div>
  </section>

  <footer>
    <div class="container">
      © ${new Date().getFullYear()} ${escapeHtml(lead.businessName)} · ${escapeHtml(lead.location)}
    </div>
  </footer>
</body>
</html>`;
}
